"use client"

import { useEffect, useState } from "react"
import { useSearchParams, useRouter } from "next/navigation"
import { Star, Heart, ShoppingCart, Grid, List, Filter, Search, Loader2 } from "lucide-react"
import Image from "next/image"
import Link from "next/link"
import Header from "@/components/header"
import { useCart } from "@/hooks/use-cart"
import { fetchProducts, getImageUrl, fetchAllCategories, fetchAllBrands, fetchAllConditions, fetchAllSubcategories, fetchFilteredProducts, calculateDiscountedPrice, isDiscountActive } from "@/lib/utils"
import DiscountBadge from "@/components/discount-badge"

type Product = {
  id: number
  name: string
  price: number
  originalPrice?: number | null
  rating?: number
  reviews?: number
  image?: string
  images?: string[]
  category?: string
  brand?: string
  condition?: string
  inStock?: boolean
  stockAvailable?: number
  discount?: number | null
  description?: string
  packSize?: string
  barcode?: string
  prescriptionOnly?: boolean
  isVatable?: boolean
  isDiscounted?: boolean
}

export default function ShopPage() {
  const searchParams = useSearchParams()
  const { addItem, state } = useCart()
  const router = useRouter()

  // Initialize filters from URL parameters
  const getInitialFilters = () => {
    const categoryParam = searchParams.get("category")
    const brandParam = searchParams.get("brand")
    const conditionParam = searchParams.get("condition")
    const subcategoryParam = searchParams.get("subcategory")
    const searchParam = searchParams.get("search")
    const sortParam = searchParams.get("sort")
    const pageParam = searchParams.get("page")
    const inStockParam = searchParams.get("inStock")
    const minPriceParam = searchParams.get("minPrice")
    const maxPriceParam = searchParams.get("maxPrice")

    return {
      category: categoryParam || "All",
      brand: brandParam || "All",
      condition: conditionParam || "All", 
      subcategory: subcategoryParam || "All",
      priceRange: [
        minPriceParam ? parseInt(minPriceParam) : 0,
        maxPriceParam ? parseInt(maxPriceParam) : 10000
      ],
      inStock: inStockParam === "true",
    }
  }

  const [products, setProducts] = useState<Product[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(parseInt(searchParams.get("page") || "1"))
  const [lastPage, setLastPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid")
  const [sortBy, setSortBy] = useState(searchParams.get("sort") || "newest")
  const [filters, setFilters] = useState(getInitialFilters())
  const [filterData, setFilterData] = useState<{
    categories: Array<{ id: number; name: string }>
    brands: Array<{ id: number; name: string }>
    conditions: Array<{ id: number; name: string }>
    subcategories: Array<{ id: number; category: string }>
  }>({
    categories: [],
    brands: [],
    conditions: [],
    subcategories: [],
  })
  const [showFilters, setShowFilters] = useState(false)

  // Handle URL parameter changes and fetch data
  useEffect(() => {
    const newPage = parseInt(searchParams.get("page") || "1")
    const newSortBy = searchParams.get("sort") || "newest"
    const newFilters = getInitialFilters()
    
    console.log('URL params changed:', { newPage, newSortBy, newFilters })
    
    setPage(newPage)
    setSortBy(newSortBy)
    setFilters(newFilters)
    
    // Fetch data after updating state
    const fetchData = async () => {
      setLoading(true)
      setError(null)
      try {
        const search = searchParams.get("search") || ""
        
        // Check if any filters are applied
        const hasFilters = newFilters.category !== "All" || 
                          newFilters.brand !== "All" || 
                          newFilters.condition !== "All" || 
                          newFilters.subcategory !== "All" || 
                          newFilters.inStock || 
                          search || 
                          newSortBy !== "newest"

        let response;
        
        if (hasFilters) {
          console.log('Using filtered products endpoint with filters:', newFilters)
          // Use filtered products endpoint when filters are applied
          response = await fetchFilteredProducts({
            category_id: newFilters.category !== "All" ? parseInt(newFilters.category) : undefined,
            brand_id: newFilters.brand !== "All" ? parseInt(newFilters.brand) : undefined,
            condition_id: newFilters.condition !== "All" ? parseInt(newFilters.condition) : undefined,
            subcategory_id: newFilters.subcategory !== "All" ? parseInt(newFilters.subcategory) : undefined,
            min_price: newFilters.priceRange[0],
            max_price: newFilters.priceRange[1],
            in_stock: newFilters.inStock,
            search: search || undefined,
            sort_by: newSortBy,
            page: newPage,
            per_page: 12,
          })
        } else {
          console.log('Using regular products endpoint, page:', newPage)
          // Use regular products endpoint when no filters
          response = await fetchProducts(newPage)
        }

        console.log('API response:', response)
        console.log('Response data:', response.data)
        console.log('Response data length:', response.data?.length)
        
        // Check if response has data
        if (!response.data) {
          console.error('No data in response:', response)
          setError("No data received from server.")
          return
        }
        
        // Convert object to array if necessary (Laravel pagination sometimes returns objects)
        let productsArray = response.data
        if (!Array.isArray(response.data) && typeof response.data === 'object') {
          console.log('Converting object response to array')
          productsArray = Object.values(response.data)
        }
        
        // Check if response.data is an array
        if (!Array.isArray(productsArray)) {
          console.error('Response data is not an array:', productsArray)
          setError("Invalid data format received from server.")
          return
        }
        
        // Map API response to frontend format
        const mappedProducts = productsArray.map((product: any) => {
          console.log('Processing product:', product)
          return {
            id: product.id,
            name: product.product_name || product.name || product.code || 'Product',
            price: Number(product.selling_price || product.price || 0),
            originalPrice: product.compare_price ? Number(product.compare_price) : undefined,
            rating: product.rating ?? 0,
            reviews: product.reviews ?? 0,
            image: getImageUrl(product.image || (product.images && product.images.length > 0 ? product.images[0].image : undefined)),
            images: product.images ? product.images.map((img: any) => getImageUrl(img.image)).filter(Boolean) : [],
            category: product.category || '',
            brand: product.brand || '',
            condition: product.condition || '',
            inStock: product.in_stock ?? (product.stock_available > 0) ?? true,
            stockAvailable: product.stock_available ?? 0,
            discount: product.discount ?? 0,
            description: product.description || product.short_description || '',
            packSize: product.pack_size || '',
            barcode: product.barcode || '',
            prescriptionOnly: product.prescription_only || false,
            isVatable: product.is_vatable || false,
            isDiscounted: product.is_discounted || false,
          }
        })

        console.log('Mapped products:', mappedProducts)
        console.log('Mapped products length:', mappedProducts.length)

        setProducts(mappedProducts)
        setLastPage(response.last_page || 1)
        setTotal(response.total || 0)
      } catch (err) {
        console.error('Error fetching products:', err)
        setError("Failed to load products.")
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [searchParams])

  useEffect(() => {
    const fetchFilterData = async () => {
      try {
        // Fetch filter options from API
        const [categoriesData, brandsData, conditionsData, subcategoriesData] = await Promise.all([
          fetchAllCategories(),
          fetchAllBrands(),
          fetchAllConditions(),
          fetchAllSubcategories()
        ])

        setFilterData({
          categories: (categoriesData.data || []).map((cat: any) => ({ id: cat.id, name: cat.name || cat.category || '' })),
          brands: (brandsData.data || []).map((brand: any) => ({ id: brand.id, name: brand.name || brand.brand || '' })),
          conditions: (conditionsData.data || []).map((condition: any) => ({ id: condition.id, name: condition.name || condition.condition || '' })),
          subcategories: (subcategoriesData.data || []).map((subcat: any) => ({ id: subcat.id, name: subcat.name || subcat.subcategory || '' })),
        })
      } catch (error) {
        console.error("Failed to fetch filter data:", error)
      }
    }

    fetchFilterData()
  }, [])

  const filteredProducts = products // No longer need local filtering since API handles it

  const updateFilter = (key: string, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }))
    
    // Update URL parameters for filters
    const params = new URLSearchParams(searchParams.toString())
    
    if (key === 'category' && value !== 'All') {
      params.set('category', value)
    } else if (key === 'category' && value === 'All') {
      params.delete('category')
    }
    
    if (key === 'brand' && value !== 'All') {
      params.set('brand', value)
    } else if (key === 'brand' && value === 'All') {
      params.delete('brand')
    }
    
    if (key === 'condition' && value !== 'All') {
      params.set('condition', value)
    } else if (key === 'condition' && value === 'All') {
      params.delete('condition')
    }
    
    if (key === 'subcategory' && value !== 'All') {
      params.set('subcategory', value)
    } else if (key === 'subcategory' && value === 'All') {
      params.delete('subcategory')
    }
    
    if (key === 'priceRange') {
      params.set('minPrice', value[0].toString())
      params.set('maxPrice', value[1].toString())
    }
    
    if (key === 'inStock') {
      if (value) {
        params.set('inStock', 'true')
      } else {
        params.delete('inStock')
      }
    }
    
    // Reset to page 1 when filters change
    params.set('page', '1')
    
    router.push(`/shop?${params.toString()}`)
  }

  const clearFilters = () => {
    setFilters({
      category: "All",
      brand: "All",
      condition: "All",
      subcategory: "All",
      priceRange: [0, 10000],
      inStock: false,
    })
    
    // Clear all filter parameters from URL
    const params = new URLSearchParams(searchParams.toString())
    params.delete('category')
    params.delete('brand')
    params.delete('condition')
    params.delete('subcategory')
    params.delete('minPrice')
    params.delete('maxPrice')
    params.delete('inStock')
    params.set('page', '1')
    
    router.push(`/shop?${params.toString()}`)
  }

  const handleAddToCart = (product: Product) => {
    addItem({
      id: product.id,
      name: product.name,
      price: product.price,
      originalPrice: product.originalPrice ?? undefined,
      image: product.image ?? "",
      inStock: product.inStock ?? false,
      is_vatable: product.isVatable ?? false,
    })
  }

  // Function to update URL with new page
  const updatePageInUrl = (newPage: number) => {
    const params = new URLSearchParams(searchParams.toString())
    params.set('page', newPage.toString())
    router.push(`/shop?${params.toString()}`)
  }

  // Generate pagination buttons
  const generatePaginationButtons = () => {
    
    const pageButtons = []
    let start = Math.max(1, page - 2)
    let end = Math.min(lastPage, page + 2)
    
    if (page <= 3) {
      end = Math.min(5, lastPage)
    } else if (page >= lastPage - 2) {
      start = Math.max(1, lastPage - 4)
    }
    
    if (start > 1) {
      pageButtons.push(
        <span key="start-ellipsis" className="px-2">...</span>
      )
    }
    
    for (let p = start; p <= end; p++) {
      pageButtons.push(
        <button
          key={p}
          onClick={() => updatePageInUrl(p)}
          className={`px-3 py-1 rounded ${p === page ? "bg-primary text-white" : "bg-gray-100 text-gray-700"}`}
        >
          {p}
        </button>
      )
    }
    
    if (end < lastPage) {
      pageButtons.push(
        <span key="end-ellipsis" className="px-2">...</span>
      )
    }
    
    return pageButtons
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="container mx-auto px-4 py-8">

        {/* Header */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Shop Products</h1>
            <p className="text-gray-600">
              {total} products found
              {searchParams.get("search") && ` for "${searchParams.get("search")}"`}
            </p>
          </div>

          <div className="flex items-center gap-4 mt-4 lg:mt-0">
            {/* View Mode Toggle */}
            <div className="flex border border-gray-300 rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode("grid")}
                className={`p-2 ${viewMode === "grid" ? "bg-primary text-white" : "bg-white text-gray-600"}`}
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`p-2 ${viewMode === "list" ? "bg-primary text-white" : "bg-white text-gray-600"}`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>

            {/* Sort Dropdown */}
            <select
              value={sortBy}
              onChange={(e) => {
                const params = new URLSearchParams(searchParams.toString())
                params.set('sort', e.target.value)
                router.push(`/shop?${params.toString()}`)
              }}
              className="border border-gray-300 rounded-lg px-3 py-2 bg-white"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="price_low">Price: Low to High</option>
              <option value="price_high">Price: High to Low</option>
              <option value="name">Name: A to Z</option>
              <option value="rating">Highest Rated</option>
            </select>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 border border-gray-300 rounded-lg px-3 py-2 bg-white hover:bg-gray-50"
            >
              <Filter className="w-4 h-4" />
              Filters
            </button>
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="bg-white rounded-lg p-6 mb-8 shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
              {/* Category Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                <select
                  value={filters.category}
                  onChange={(e) => updateFilter("category", e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="All">All Categories</option>
                  {filterData.categories.map((category) => (
                    <option key={category.id} value={category.id}>{category.name}</option>
                  ))}
                </select>
              </div>

              {/* Brand Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Brand</label>
                <select
                  value={filters.brand}
                  onChange={(e) => updateFilter("brand", e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="All">All Brands</option>
                  {filterData.brands.map((brand) => (
                    <option key={brand.id} value={brand.id}>{brand.name}</option>
                  ))}
                </select>
              </div>

              {/* Condition Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Condition</label>
                <select
                  value={filters.condition}
                  onChange={(e) => updateFilter("condition", e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="All">All Conditions</option>
                  {filterData.conditions.map((condition) => (
                    <option key={condition.id} value={condition.id}>{condition.name}</option>
                  ))}
                </select>
              </div>

              {/* Subcategory Filter */}
              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Subcategory</label>
                <select
                  value={filters.subcategory}
                  onChange={(e) => updateFilter("subcategory", e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="All">All Subcategories</option>
                  {filterData.subcategories.map((subcategory) => (
                    <option key={subcategory.id} value={subcategory.id}>{subcategory.category}</option>
                  ))}
                </select>
              </div> */}

              {/* Price Range Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Price Range</label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    placeholder="Min"
                    value={filters.priceRange[0]}
                    onChange={(e) => updateFilter("priceRange", [Number(e.target.value), filters.priceRange[1]])}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                  <input
                    type="number"
                    placeholder="Max"
                    value={filters.priceRange[1]}
                    onChange={(e) => updateFilter("priceRange", [filters.priceRange[0], Number(e.target.value)])}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
              </div>
            </div>

            {/* In Stock Filter */}
            <div className="mt-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={filters.inStock}
                  onChange={(e) => updateFilter("inStock", e.target.checked)}
                  className="rounded border-gray-300"
                />
                <span className="text-sm font-medium text-gray-700">In Stock Only</span>
              </label>
            </div>

            {/* Clear Filters */}
            <div className="mt-4">
              <button
                onClick={clearFilters}
                className="text-sm text-primary hover:text-primary-600"
              >
                Clear All Filters
              </button>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="flex items-center gap-2">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
              <span className="text-lg text-gray-600">Loading products...</span>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-12">
            <p className="text-lg text-red-600 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary-600"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Products Grid */}
        {!loading && !error && (
          <>
            {products.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-lg text-gray-600 mb-4">No products found matching your criteria.</p>
                <button
                  onClick={clearFilters}
                  className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary-600"
                >
                  Clear Filters
                </button>
              </div>
            ) : (
              <div className={viewMode === "grid" ? "grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4" : "space-y-4"}>
                {products.map((product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    viewMode={viewMode}
                    onAddToCart={handleAddToCart}
                  />
                ))}
              </div>
            )}

            {/* Pagination */}
            {lastPage > 1 && (
              <div className="flex justify-center items-center gap-2 mt-8">
                <button
                  onClick={() => updatePageInUrl(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="px-3 py-1 rounded bg-gray-100 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                {generatePaginationButtons()}
                <button
                  onClick={() => updatePageInUrl(Math.min(lastPage, page + 1))}
                  disabled={page === lastPage}
                  className="px-3 py-1 rounded bg-gray-100 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

function ProductCard({ product, viewMode, onAddToCart }: { product: Product; viewMode: string; onAddToCart: (product: Product) => void }) {
  const { state } = useCart()
  
  const addToWishlist = () => {
    // Add to wishlist functionality
    console.log("Adding to wishlist:", product.id)
  }

  // Safety check for product data
  if (!product || typeof product !== 'object') {
    console.warn('Invalid product data in ProductCard:', product)
    return null
  }

  // Calculate discounted price if discount is active
  const originalPrice = product.price || 0
  const { discountedPrice, discountAmount } = calculateDiscountedPrice(originalPrice)
  const isDiscountApplied = isDiscountActive() && discountAmount > 0

  // Ensure required fields exist
  const safeProduct = {
    id: product.id || 0,
    name: product.name || 'Product',
    price: isDiscountApplied ? discountedPrice : originalPrice,
    originalPrice: isDiscountApplied ? originalPrice : product.originalPrice,
    rating: product.rating || 0,
    reviews: product.reviews || 0,
    image: product.image || '/banners/no-img.jpg',
    images: product.images || [],
    category: product.category || '',
    brand: product.brand || '',
    condition: product.condition || '',
    inStock: product.inStock ?? true,
    discount: product.discount || 0,
    description: product.description || '',
  }

  if (viewMode === "list") {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6 flex gap-6">
        <div className="w-32 h-32 flex-shrink-0 relative">
          <Image
            src={safeProduct.image}
            alt={safeProduct.name}
            width={128}
            height={128}
            className="w-full h-full object-cover rounded-lg"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = "/banners/no-img.jpg";
            }}
          />
          <DiscountBadge className="top-1 left-1" />
        </div>
        <div className="flex-1">
          <div className="flex justify-between items-start mb-2">
            <Link href={`/product/${safeProduct.id}`}>
              <h3 className="font-semibold text-gray-900 hover:text-primary cursor-pointer">{safeProduct.name}</h3>
            </Link>
            <button onClick={addToWishlist} className="text-gray-400 hover:text-red-500">
              <Heart className="w-5 h-5" />
            </button>
          </div>
          <p className="text-sm text-gray-600 mb-2">{safeProduct.description}</p>
          <div className="flex items-center gap-2 mb-2">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className={`w-4 h-4 ${
                    i < Math.floor(safeProduct.rating) ? "fill-yellow-400 text-yellow-400" : "text-gray-300"
                  }`}
                />
              ))}
            </div>
            <span className="text-sm text-gray-500">({safeProduct.reviews})</span>
          </div>
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <span className="text-base sm:text-lg font-bold text-green-600">KSh {safeProduct.price.toLocaleString()}</span>
              {safeProduct.originalPrice && (
                <span className="text-xs sm:text-sm text-gray-500 line-through">
                  {safeProduct.originalPrice > safeProduct.price && (
                    <span>
                      KSh {safeProduct.originalPrice.toLocaleString()}
                    </span>
                  )}
                </span>
              )}
            </div>
                    <button
          onClick={() => onAddToCart(safeProduct)}
          disabled={!safeProduct.inStock || state.isLoading}
          className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-600 disabled:bg-gray-300 flex items-center gap-2 text-sm"
        >
          {state.isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <ShoppingCart className="w-4 h-4" />
          )}
          {state.isLoading ? "Adding..." : (safeProduct.inStock ? "Add to Cart" : "Out of Stock")}
        </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden group hover:shadow-lg transition-shadow">
      <div className="relative">
        <Link href={`/product/${safeProduct.id}`}>
          <Image
            src={safeProduct.image}
            alt={safeProduct.name}
            width={300}
            height={300}
            className="w-full h-40 sm:h-48 object-cover group-hover:scale-105 transition-transform duration-300"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = "/banners/no-img.jpg";
            }}
          />
        </Link>
        <DiscountBadge />
        {!safeProduct.inStock && (
          <div className="absolute top-2 right-2 bg-gray-500 text-white text-xs px-2 py-1 rounded">Out of Stock</div>
        )}
        <button
          onClick={addToWishlist}
          className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-white rounded-full p-2 shadow-md"
        >
          <Heart className="w-4 h-4 text-gray-600 hover:text-red-500" />
        </button>
      </div>

      <div className="p-3 sm:p-4">
        {/* <div className="text-xs text-primary mb-1">{safeProduct.category}</div> */}
        <Link href={`/product/${safeProduct.id}`}>
          <h3 className="text-sm font-semibold text-gray-900 mb-2 hover:text-primary cursor-pointer line-clamp-2">
            {safeProduct.name}
          </h3>
        </Link>

        <div className="flex items-center gap-1 mb-2">
          <div className="flex">
            {[...Array(5)].map((_, i) => (
              <Star
                key={i}
                className={`w-4 h-4 ${
                  i < Math.floor(safeProduct.rating) ? "fill-yellow-400 text-yellow-400" : "text-gray-300"
                }`}
              />
            ))}
          </div>
          <span className="text-sm text-gray-500">({safeProduct.reviews})</span>
        </div>

        <div className="flex items-center gap-2 mb-3 sm:mb-4">
          <span className="text-base sm:text-lg font-bold text-green-600">KSh {safeProduct.price.toLocaleString()}</span>
          {safeProduct.originalPrice && (
            <span className="text-xs sm:text-sm text-gray-500 line-through">
              {safeProduct.originalPrice > safeProduct.price && (
                <span>
                  KSh {safeProduct.originalPrice.toLocaleString()}
                </span>
              )}
            </span>
          )}
        </div>

        <button
          onClick={() => onAddToCart(safeProduct)}
          disabled={!safeProduct.inStock || state.isLoading}
          className="w-full bg-primary text-white py-2 rounded-lg hover:bg-primary-600 disabled:bg-gray-300 flex items-center justify-center gap-2 text-sm"
        >
          {state.isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <ShoppingCart className="w-4 h-4" />
          )}
          {state.isLoading ? "Adding..." : (safeProduct.inStock ? "Add to Cart" : "Out of Stock")}
        </button>
      </div>
    </div>
  )
}
